import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type {
  IrregularPolygonElementData,
  OvalElementData,
  PolylineElementData,
  RectangleElementData,
  RegularPolygonElementData,
} from '@/api/types/stowed-element'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { shapeToPathCommands } from '@/lib/elements/shape-path-commands'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import { normInset, normSides } from '@/lib/object-effects/sides'
import { BuildStowedShapeOptions } from '../types/build-stowed-shape-options'
import { buildShapeContent } from './build-shape-content'
import { buildLineStyle, buildShapeFillStyle } from './paint-defaults'
import { buildBase } from './build-base'
import { buildTextWrap } from './build-text-wrap'
import { buildShapeTextContent } from './build-shape-text-content'
import { convertPathCommands } from './convert-path-commands'
import { isClosedPenPathShape } from './is-closed-pen-path-shape'
export function buildRectangleElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): RectangleElementData {
  const rc = shape.roundedCorner
  const roundedCorner =
    typeof rc === 'number' && Number.isFinite(rc) ? Math.max(0, Math.min(100, rc)) : 0
  const content = buildShapeContent(shape, fonts, options?.shapeText)
  const fillStyle = buildShapeFillStyle(shape)

  return {
    base: buildBase(shape, options, projection),
    lineStyle: buildLineStyle(shape),
    roundedCorner,
    ...(fillStyle ? { fillStyle } : {}),
    ...(content ? { content } : {}),
    ...buildTextWrap(shape),
  }
}

export function buildOvalElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION
): OvalElementData {
  const content = buildShapeTextContent(shape, undefined, options?.shapeText)
  const fillStyle = buildShapeFillStyle(shape)

  return {
    base: buildBase(shape, options, projection),
    lineStyle: buildLineStyle(shape),
    ...(fillStyle ? { fillStyle } : {}),
    ...(content ? { content } : {}),
  }
}

export function buildIrregularPolygonElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): IrregularPolygonElementData {
  const hasCommands = Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0
  const commands = hasCommands
    ? shape.pathCommands!
    : shapeToPathCommands(shape.type, shape.width, shape.height)
  const content = buildShapeContent(shape, fonts, options?.shapeText)
  const fillStyle = buildShapeFillStyle(shape)

  return {
    base: buildBase(shape, options, projection),
    path: { commands: convertPathCommands(commands, shape.width, shape.height) },
    lineStyle: buildLineStyle(shape),
    ...(fillStyle ? { fillStyle } : {}),
    ...(content ? { content } : {}),
    ...buildTextWrap(shape),
  }
}

export function buildPolylineElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): PolylineElementData {
  const hasCommands = Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0
  const commands = hasCommands
    ? shape.pathCommands!
    : shapeToPathCommands(shape.type, shape.width, shape.height)

  const isClosed = isClosedPenPathShape(shape)
  const content = isClosed
    ? buildShapeContent(shape, fonts, options?.shapeText)
    : buildShapeTextContent(shape, fonts, options?.shapeText)

  return {
    base: buildBase(shape, options, projection),
    path: { commands: convertPathCommands(commands, shape.width, shape.height) },
    lineStyle: buildLineStyle(shape),
    ...(isClosed ? { isClosed: true } : {}),
    ...(content ? { content } : {}),
    ...buildTextWrap(shape),
  }
}

export function buildRegularPolygonElement(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): RegularPolygonElementData {
  const content = buildShapeContent(shape, fonts, options?.shapeText)
  const fillStyle = buildShapeFillStyle(shape)

  return {
    base: buildBase(shape, options, projection),
    sides: normSides(shape),
    // Inset is persisted in `inner` for regular polygons.
    inner: String(normInset(shape)),
    // RegularPolygonElement has no line_style in the NSD/proto, so fill is its only visible paint
    // attribute — without it yb360 renders the polygon invisibly (text only). Omitted for a photo
    // fill (don't paint over the photo) or an explicit None fill.
    ...(fillStyle ? { fillStyle } : {}),
    ...(content ? { content } : {}),
    ...buildTextWrap(shape),
  }
}
